import { useState, useMemo } from "react";
import { Link } from "wouter";
import { useListMembersContent } from "@workspace/api-client-react";
import type { MembersContent } from "@workspace/api-client-react";
import {
  Search, X, Play, Eye, Heart, Layers, Box, Mic, Radio,
  Image as ImageIcon, Zap, ArrowLeft,
} from "lucide-react";
import { Helmet } from "react-helmet-async";
import { ContentOrientationToggle, type ContentOrientation } from "@/components/ContentOrientationToggle";
import { AdultContentToggle } from "@/components/AdultContentToggle";
import { AdultLockedLink, AdultLockOverlay } from "@/components/AdultLockedLink";

const SLUG_MAP: Record<number, string> = {
  7: "illusionistz",
  8: "emeraldonyx",
  9: "mistyhaze",
  10: "claudiag",
  11: "avtheentertainer",
  12: "germani",
  13: "mst",
  14: "goldie",
  18: "rhythmandsoulpatrol",
  19: "joerussketchbook",
};

const TIER_STYLES: Record<string, string> = {
  free: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30",
  standard: "bg-blue-500/20 text-blue-300 border border-blue-500/30",
  premium: "bg-violet-500/20 text-violet-300 border border-violet-500/30",
};

const MEDIA_ICONS: Record<string, typeof Play> = {
  video: Play, audio: Mic, livestream: Radio,
  image: ImageIcon, photo: ImageIcon,
  series: Layers, virtual: Box,
};

const MEDIA_LABEL: Record<string, string> = {
  video: "Video", audio: "Audio", livestream: "Live",
  image: "Photo", photo: "Photo", series: "Series", virtual: "4D Space",
};

const TIER_OPTIONS = ["all", "free", "standard", "premium"];
const TYPE_OPTIONS = ["all", "video", "photo", "series", "virtual", "audio"];

function ContentCard({ item }: { item: MembersContent }) {
  const slug = SLUG_MAP[item.creatorId] ?? String(item.creatorId);
  const Icon = MEDIA_ICONS[item.mediaType] ?? Play;
  const tier = TIER_STYLES[item.tier] ?? TIER_STYLES.free;
  const isVideo = item.mediaType === "video" || item.mediaType === "series";

  return (
    <AdultLockedLink item={item} href={`/creators/${slug}/post/${item.id}`}>
      <div className="group relative rounded-xl overflow-hidden bg-card border border-border hover:border-primary/40 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-primary/10 cursor-pointer">
        <div className="relative aspect-video overflow-hidden">
          {item.thumbnailUrl ? (
            <img
              src={item.thumbnailUrl}
              alt={item.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full bg-zinc-900 flex items-center justify-center">
              <Icon className="w-8 h-8 text-zinc-700" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
          {item.isAdult && <AdultLockOverlay />}
          {isVideo && (
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="w-11 h-11 rounded-full bg-white/20 backdrop-blur-sm border border-white/20 flex items-center justify-center">
                <Play className="w-5 h-5 text-white fill-white ml-0.5" />
              </div>
            </div>
          )}
          <div className="absolute top-2 left-2 flex items-center gap-1.5">
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded uppercase tracking-wider ${tier}`}>
              {item.tier}
            </span>
          </div>
          <div className="absolute top-2 right-2 bg-black/60 rounded-full p-1.5">
            <Icon className="w-3 h-3 text-white" />
          </div>
          <div className="absolute bottom-2 left-2 right-2 flex items-center gap-3">
            <span className="flex items-center gap-1 text-white/70 text-[10px] font-mono">
              <Eye className="w-3 h-3" />
              {item.viewCount >= 1000 ? `${(item.viewCount / 1000).toFixed(1)}k` : item.viewCount}
            </span>
            <span className="flex items-center gap-1 text-white/70 text-[10px] font-mono">
              <Heart className="w-3 h-3" />
              {item.likeCount >= 1000 ? `${(item.likeCount / 1000).toFixed(1)}k` : item.likeCount}
            </span>
            <span className="ml-auto flex items-center gap-1 text-primary text-[10px] font-mono">
              <Zap className="w-3 h-3" />
              {Number(item.gasFeeGlo).toFixed(4)} GLO
            </span>
          </div>
        </div>
        <div className="p-3">
          <span className="text-[10px] font-mono text-primary uppercase tracking-widest">
            {MEDIA_LABEL[item.mediaType] ?? item.mediaType}
          </span>
          <p className="text-sm font-medium text-white line-clamp-2 leading-snug mt-0.5 mb-2">
            {item.title}
          </p>
          <div className="flex items-center gap-2">
            {item.creatorAvatar && (
              <img
                src={item.creatorAvatar}
                alt={item.creatorName}
                className="w-5 h-5 rounded-full object-cover border border-border/40 flex-shrink-0"
              />
            )}
            <span className="text-xs text-muted-foreground truncate">{item.creatorName}</span>
          </div>
        </div>
      </div>
    </AdultLockedLink>
  );
}

function FilterChip({
  label, active, onClick,
}: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium capitalize border transition-colors ${
        active
          ? "bg-primary border-primary text-white"
          : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/20"
      }`}
    >
      {label === "all" ? "All" : label.charAt(0).toUpperCase() + label.slice(1)}
    </button>
  );
}

export default function ContentGallery() {
  const [search, setSearch] = useState("");
  const [creatorId, setCreatorId] = useState<number | "all">("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [tierFilter, setTierFilter] = useState("all");
  const [orientation, setOrientation] = useState<ContentOrientation>("day");
  const [showAdult, setShowAdult] = useState(false);

  const { data: allContent, isLoading } = useListMembersContent({ orientation, showAdult });

  async function changeOrientation(next: ContentOrientation) {
    setOrientation(next);
  }

  const creators = useMemo(() => {
    if (!allContent) return [];
    const map = new Map<number, { id: number; name: string; avatar: string | null }>();
    allContent.forEach((c) => {
      if (!map.has(c.creatorId)) {
        map.set(c.creatorId, { id: c.creatorId, name: c.creatorName, avatar: c.creatorAvatar ?? null });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allContent]);

  const availableTypes = useMemo(() => {
    if (!allContent) return TYPE_OPTIONS;
    const found = new Set(allContent.map((c) => c.mediaType));
    return ["all", ...TYPE_OPTIONS.slice(1).filter((t) => found.has(t as import("@workspace/api-client-react").MembersContentMediaType))];
  }, [allContent]);

  const filtered = useMemo(() => {
    if (!allContent) return [];
    const q = search.trim().toLowerCase();
    return allContent.filter((c) => {
      if (creatorId !== "all" && c.creatorId !== creatorId) return false;
      if (typeFilter !== "all" && c.mediaType !== typeFilter) return false;
      if (tierFilter !== "all" && c.tier !== tierFilter) return false;
      if (q && !c.title.toLowerCase().includes(q) && !c.creatorName.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [allContent, creatorId, typeFilter, tierFilter, search]);

  const hasFilters = search !== "" || creatorId !== "all" || typeFilter !== "all" || tierFilter !== "all";

  function clearFilters() {
    setSearch("");
    setCreatorId("all");
    setTypeFilter("all");
    setTierFilter("all");
  }

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Content Gallery — ISM | New World</title>
        <meta name="description" content="Browse all creator content on ISM New World — filter by creator, type, and tier." />
      </Helmet>

      {/* Header */}
      <div className="border-b border-border bg-background/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="container mx-auto px-4 max-w-7xl py-4">
          <div className="flex items-center gap-3 mb-4">
            <Link href="/" className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> Home
            </Link>
            <span className="text-border">/</span>
            <span className="text-xs text-foreground">Content Gallery</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-end gap-4 mb-4">
            <div>
              <p className="text-primary text-xs font-mono uppercase tracking-widest mb-1">ISM | New World</p>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tighter uppercase leading-none">
                Content Gallery
              </h1>
            </div>
            {/* Search */}
            <div className="md:ml-auto relative w-full md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search posts or creators…"
                className="w-full pl-9 pr-8 py-2 rounded-lg bg-card border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50 transition-colors"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Filter rows */}
          <div className="space-y-2.5">
            <div className="flex items-center gap-3">
              <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider flex-shrink-0 w-14">View</span>
              <ContentOrientationToggle value={orientation} onChange={changeOrientation} />
              <AdultContentToggle className="ml-auto" value={showAdult} onChange={setShowAdult} />
            </div>
            {/* Creator filter */}
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
              <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider flex-shrink-0 w-14">Creator</span>
              <FilterChip label="all" active={creatorId === "all"} onClick={() => setCreatorId("all")} />
              {creators.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCreatorId(c.id === creatorId ? "all" : c.id)}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    creatorId === c.id
                      ? "bg-primary border-primary text-white"
                      : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/20"
                  }`}
                >
                  {c.avatar && (
                    <img src={c.avatar} alt={c.name} className="w-4 h-4 rounded-full object-cover flex-shrink-0" />
                  )}
                  {c.name}
                </button>
              ))}
            </div>

            {/* Type + Tier filters */}
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
                <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider flex-shrink-0 w-14">Type</span>
                {availableTypes.map((t) => (
                  <FilterChip
                    key={t}
                    label={t === "all" ? "All" : (MEDIA_LABEL[t] ?? t)}
                    active={typeFilter === t}
                    onClick={() => setTypeFilter(t)}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider flex-shrink-0 w-14">Tier</span>
                {TIER_OPTIONS.map((t) => (
                  <FilterChip key={t} label={t} active={tierFilter === t} onClick={() => setTierFilter(t)} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="container mx-auto px-4 max-w-7xl py-8">
        {/* Result count + clear */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-muted-foreground">
            {isLoading ? (
              "Loading…"
            ) : (
              <>
                <strong className="text-foreground">{filtered.length}</strong>{" "}
                {filtered.length === 1 ? "post" : "posts"}
                {hasFilters && " matching your filters"}
              </>
            )}
          </p>
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors border border-border/50 px-2.5 py-1 rounded-lg hover:border-foreground/20"
            >
              <X className="w-3 h-3" /> Clear filters
            </button>
          )}
        </div>

        {/* Grid */}
        {isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {Array.from({ length: 20 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="aspect-video bg-card/50 animate-pulse rounded-xl" />
                <div className="h-3 bg-card/50 animate-pulse rounded w-4/5" />
                <div className="h-3 bg-card/50 animate-pulse rounded w-2/5" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <Search className="w-10 h-10 text-muted-foreground/30 mb-4" />
            <p className="text-muted-foreground text-sm mb-2">No content matches your filters.</p>
            <button
              onClick={clearFilters}
              className="text-xs text-primary hover:underline mt-1"
            >
              Clear all filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 pb-16">
            {filtered.map((item) => (
              <ContentCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
